export interface JwtClaims {
  sub?: string;
  email?: string;
  name?: string;
  [claim: string]: unknown;
}

/** Zitadel project role that grants access to admin features (AITRAS project role). */
const ADMIN_ROLE = 'ADMIN';

/** Zitadel reserved claim carrying the org (tenant) id, emitted by the resourceowner scope. */
const TENANT_ID_CLAIM = 'urn:zitadel:iam:user:resourceowner:id';

export function decodeJwt(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replaceAll('-', '+').replaceAll('_', '/');
    // atob yields one char per byte (all < 0x100); decode those bytes as UTF-8.
    // `fatal` makes malformed UTF-8 throw (→ null) instead of decoding to U+FFFD.
    const bytes = Uint8Array.from(atob(base64), (c) => c.codePointAt(0)!);
    const json = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * Admin access is granted via the AITRAS Zitadel project role "ADMIN".
 * Zitadel emits project roles as an object under `urn:zitadel:iam:org:project:roles`
 * (and the project-scoped `urn:zitadel:iam:org:project:{id}:roles`); each granted
 * role name is a key in that object. We treat the token as admin when ADMIN appears
 * in any such project-roles claim.
 */
export function isAdminToken(token: string | null): boolean {
  if (!token) return false;
  const claims = decodeJwt(token);
  if (!claims) return false;
  return Object.entries(claims).some(
    ([key, value]) =>
      key.startsWith('urn:zitadel:iam:org:project') &&
      key.endsWith(':roles') &&
      typeof value === 'object' &&
      value !== null &&
      Object.hasOwn(value, ADMIN_ROLE),
  );
}

/** The logged-in identity, derived from the ID token claims (AITRAS exposes no /me). */
export interface Identity {
  subject: string;
  tenantId: string | null;
  displayName: string;
  email: string;
  initials: string;
}

function deriveInitials(name: string, email: string): string {
  const source = name.trim() || email.trim();
  if (!source) return 'U';
  const words = source.split(/[\s@._-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/** Build the identity from the current bearer token's claims (client-side). */
export function identityFromToken(token: string | null): Identity | null {
  if (!token) return null;
  const claims = decodeJwt(token);
  if (!claims?.sub) return null;
  const displayName = (claims.name as string) || (claims.email as string) || claims.sub;
  const email = (claims.email as string) || '';
  const tenantRaw = claims[TENANT_ID_CLAIM];
  const tenantId = typeof tenantRaw === 'string' && tenantRaw ? tenantRaw : null;
  return {
    subject: claims.sub,
    tenantId,
    displayName,
    email,
    initials: deriveInitials(typeof claims.name === 'string' ? claims.name : '', email),
  };
}
